import { List, Map } from "immutable";
import moment from "moment";

import { isStudentAssignment } from "seneca-common/features/assignments/common/typeGuards";
import { getAssigneeStatusCode } from "seneca-common/features/assignments/common/utils";
import {
  getAssignmentProgress,
  getAssignmentUserId
} from "seneca-common/features/assignments/features/student-assignments/state/selectors/state/assignments/assignment/studentAssignment";
import { getNumberOfAssignees } from "seneca-common/features/assignments/features/teacher-assignments/state/selectors/state/assignments/assignment/teacherAssignment";
import sortAscendingOrder from "seneca-common/utils/functions/sorting/sortAscendingOrder";
import sortDescendingOrder from "seneca-common/utils/functions/sorting/sortDescendingOrder";
import { composeSelector } from "seneca-common/utils/selectors/compose-selectors";

import Assignment from "../../../models/Assignment";
import "../../../models/spec/Spec";
import {
  assignmentHasNoSections as getAssignmentHasNoSections,
  assignmentIncludesExamQuestions as getAssignmentIncludesExamQuestions,
  getAssignmentCourseId as getCourseId,
  getAssignmentCutoffDate as getCutoffDate,
  getAssignmentExamQuestionIds as getExamQuestionIds,
  getAssignmentId as getId,
  getAssignmentNumberOfExamQuestions as getNumberOfExamQuestions,
  getAssignmentNumberOfSections as getNumberOfSections,
  getAssignmentSectionIds as getSectionIds,
  getClassId,
  getDueDate,
  getName,
  getNumAssignees,
  getSpec,
  getSpecType,
  getStartDate,
  getStatus,
  isAssignmentAfterCutoffDate,
  isFutureAssignment,
  isPastAssignment,
  isSmart,
  isStatusComplete,
  isStatusIncomplete,
  isStatusLate
} from "./assignment";

type AssignmentId = string;
type SectionId = string;
type ClassId = string;
type CourseId = string;
type State = Map<AssignmentId, Assignment>;

export function getAssignment(assignments: State, assignmentId: AssignmentId) {
  return assignments.get(assignmentId);
}

export function getLastAssignmentFromSectionId(
  assignments: State,
  sectionId: SectionId
) {
  return assignments
    .filter(assignment => {
      const sectionIdsList = getSectionIds(assignment);
      return sectionIdsList.includes(sectionId);
    })
    .sort((a1, a2) => sortDescendingOrder(getStartDate(a1), getStartDate(a2)))
    .first();
}

// @ts-ignore
export const getAssignmentId: (
  arg0: State,
  arg1: AssignmentId
  // @ts-ignore
) => AssignmentId = composeSelector(getAssignment, getId);

// @ts-ignore
export const getAssignmentSpec: (arg0: State, arg1: AssignmentId) => SpecType =
  // @ts-ignore
  composeSelector(getAssignment, getSpec);

// @ts-ignore
export const getAssignmentClassId: (arg0: State, arg1: AssignmentId) => string =
  // @ts-ignore
  composeSelector(getAssignment, getClassId);

// @ts-ignore
export const getAssignmentSpecType: (
  arg0: State,
  arg1: AssignmentId
  // @ts-ignore
) => string = composeSelector(getAssignment, getSpecType);

export const getAssignmentIsSmart: (
  arg0: State,
  arg1: AssignmentId
  // @ts-ignore
) => boolean = composeSelector(getAssignment, isSmart);

export const getAssignmentSectionLastSetDate: (
  state: State,
  sectionId: SectionId
) => string | undefined = composeSelector(
  getLastAssignmentFromSectionId,
  getStartDate
);

// @ts-ignore
export const getAssignmentStartDate: (
  arg0: State,
  arg1: AssignmentId
  // @ts-ignore
) => string = composeSelector(getAssignment, getStartDate);

// @ts-ignore
export const getAssignmentDueDate: (arg0: State, arg1: AssignmentId) => string =
  // @ts-ignore
  composeSelector(getAssignment, getDueDate);

// @ts-ignore
export const getAssignmentStatus: (
  arg0: State,
  arg1: AssignmentId
  // @ts-ignore
) => string = composeSelector(getAssignment, getStatus);

// @ts-ignore
export const getAssignmentName: (arg0: State, arg1: AssignmentId) => string =
  // @ts-ignore
  composeSelector(getAssignment, getName);

// @ts-ignore
export const getAssignmentNumOfAssignees: (
  arg0: State,
  arg1: AssignmentId
) => number =
  // @ts-ignore
  composeSelector(getAssignment, getNumAssignees);

// @ts-ignore
export const getAssignmentNumAssignees: (
  arg0: State,
  arg1: AssignmentId
) => number =
  // @ts-ignore
  composeSelector(getAssignment, getNumberOfAssignees);

export const getAssignmentCourseId: (
  arg0: State,
  arg1: AssignmentId
  // @ts-ignore
) => string = composeSelector(getAssignment, getCourseId);

export const getAssignmentSectionIds: (
  arg0: State,
  arg1: AssignmentId
  // @ts-ignore
) => List<string> = composeSelector(getAssignment, getSectionIds);

export const getAssignmentNumberOfSections: (
  arg0: State,
  arg1: AssignmentId
  // @ts-ignore
) => number = composeSelector(getAssignment, getNumberOfSections);

// @ts-ignore
export const getAssignmentCutoffDate: (
  arg0: State,
  arg1: AssignmentId
  // @ts-ignore
) => string = composeSelector(getAssignment, getCutoffDate);

export const getAssignmentExamQuestionIds: (
  arg0: State,
  arg1: AssignmentId
  // @ts-ignore
) => List<string> = composeSelector(getAssignment, getExamQuestionIds);

export const getAssignmentNumberOfExamQuestions: (
  arg0: State,
  arg1: AssignmentId
  // @ts-ignore
) => number = composeSelector(getAssignment, getNumberOfExamQuestions);

export const assignmentIncludesExamQuestions: (
  arg0: State,
  arg1: AssignmentId
) => boolean = composeSelector(
  // @ts-ignore
  getAssignment,
  getAssignmentIncludesExamQuestions
);

export const assignmentHasNoSections: (
  arg0: State,
  arg1: AssignmentId
) => boolean = composeSelector(
  // @ts-ignore
  getAssignment,
  getAssignmentHasNoSections
);

export const getClassAssignments: (arg0: State, arg1: ClassId) => State = (
  assignments,
  classId
) => assignments.filter(assignment => getClassId(assignment) === classId);

export const getClassAssignmentIds = (assignments: State, classId: string) => {
  return getClassAssignments(assignments, classId)
    .map(assignment => getId(assignment))
    .toList();
};

export function getCourseAssignments(assignments: State, courseId: CourseId) {
  return assignments.filter(assignment => getCourseId(assignment) === courseId);
}

export const hasMoreThanOneAssignment = (assignments: State) => {
  return assignments.size > 1;
};

export const getFutureAssignments = (assignments: State, now: moment.Moment) =>
  assignments
    .toList()
    .filter(assignment => isFutureAssignment(assignment, now));

export const getDoneAssignments = (assignments: State) => {
  return assignments.filter(
    assignment => isStatusComplete(assignment) || isStatusLate(assignment)
  );
};

export const getIncompleteAssignments = (assignments: State) => {
  return assignments.filter(isStatusIncomplete);
};

export const getOverdueAssignments = (
  assignments: State,
  now: moment.Moment
) => {
  return getIncompleteAssignments(assignments).filter(
    assignment =>
      isPastAssignment(assignment, now) &&
      !isAssignmentAfterCutoffDate(assignment, now)
  );
};

export const getMissedAssignments = (
  assignments: State,
  now: moment.Moment
) => {
  return getIncompleteAssignments(assignments).filter(assignment =>
    isAssignmentAfterCutoffDate(assignment, now)
  );
};

export const getNextDueAssignmentId = (
  assignments: State,
  now: moment.Moment
) => {
  const futureAssignments = getFutureAssignments(assignments, now);

  const nextDueAssignments = futureAssignments
    .sort((a1, a2) => sortAscendingOrder(getDueDate(a1), getDueDate(a2)))
    .sort((a1, a2) => {
      const a1statusCode = getAssigneeStatusCode(
        getStatus(a1)?.toLocaleLowerCase() ?? "",
        false
      );
      const a2statusCode = getAssigneeStatusCode(
        getStatus(a2)?.toLocaleLowerCase() ?? "",
        false
      );
      if (isStudentAssignment(a1) && isStudentAssignment(a2)) {
        return (
          sortAscendingOrder(a1statusCode, a2statusCode) ||
          sortDescendingOrder(
            getAssignmentProgress(a1),
            getAssignmentProgress(a2)
          )
        );
      }
      return sortAscendingOrder(a1statusCode, a2statusCode);
    });

  if (nextDueAssignments.size) return getId(nextDueAssignments.first());

  return null;
};

export const getNumberOfClassAssignments = (state: State, classId: string) => {
  return getClassAssignments(state, classId).size;
};

export const getNumberOfUpcomingClassAssignments = (
  state: State,
  classId: string,
  now: moment.Moment
) => {
  const classAssignments = getClassAssignments(state, classId);
  const futureAssignments = getFutureAssignments(classAssignments, now);
  return futureAssignments.size;
};

export const getNextDueAssignmentWithExamQuestions = (
  state: State,
  classId: string,
  now: moment.Moment
) => {
  const classAssignments = getClassAssignments(state, classId);
  const futureAssignments = getFutureAssignments(classAssignments, now);
  return futureAssignments
    .filter(assignment => getAssignmentIncludesExamQuestions(assignment))
    .sort((a1, a2) => sortAscendingOrder(getDueDate(a1), getDueDate(a2)))
    .first();
};

export const getNumberOfUpcomingClassAssignmentsWithExamQuestions = (
  state: State,
  classId: string,
  now: moment.Moment
) => {
  const classAssignments = getClassAssignments(state, classId);
  const futureAssignments = getFutureAssignments(classAssignments, now);
  return futureAssignments.filter(getAssignmentIncludesExamQuestions).size;
};

export const getNumberOfUpcomingAssignments = (assignments: State) => {
  const now = moment();
  return getFutureAssignments(assignments, now).size;
};

const getPastAssignments = (assignments: State, now: moment.Moment) =>
  assignments.toList().filter(assignment => isPastAssignment(assignment, now));

export const getNumberOfPastClassAssignments = (
  state: State,
  classId: string,
  now: moment.Moment
) => {
  const classAssignments = getClassAssignments(state, classId);
  const pastAssignments = getPastAssignments(classAssignments, now);
  return pastAssignments.size;
};

export const getAssignmentsByUserId = (assignments: State, userId: string) => {
  return assignments.filter(
    assignment => getAssignmentUserId(assignment) === userId
  );
};
